<template>
  <vx-card class="preview-container mb-10" :noShadow="true">
    <div class="float-left">
      <h5 class="font-medium text-2xl">
        {{ $t('Ad Preview') }}
      </h5>
    </div>

    <div class="back-to-ad-button float-right">
      <vs-button id="create-campaign-s5-back-to-ad-details-button" @click="value.is_previewing = false" class="pl-16" color="primary" type="border">
        <img class="back-button-icon" src="@/assets/images/chevron-left.svg" />
        {{ $t('Back to ad details') }}
      </vs-button>
    </div>

    <h5 class="ad-details-desc text-base font-normal text-gray-500 mt-10 md:mb-16 mb-4">
      {{ $t('This is how your ad will be displayed on the Speakol widget.') }}
    </h5>

    <div class="mobile-preview-button">
      <vs-button @click="value.is_previewing = false" class="w-full pl-16" color="primary" type="border">
        <img class="back-button-icon" src="@/assets/images/chevron-left.svg" />
        {{ $t('Back to ad details') }}
      </vs-button>
    </div>

    <div>
      <div class="w-full text-center md:mt-8 mt-6 ad-preview-container flex md:flex-row flex-col justify-center">
        <div v-for="(p, index) in previews" :key="'span-tag-' + index" class="span-tag md:mr-6 mb-2 flex justify-center md:flex-grow-0 flex-grow device-btn" :class="{'span-tag-active' : currently_previewing === p}" @click="changeCurrentlyPreviewing(p)">
          <span ><vs-icon icon-pack="speakol" :icon="`speakol-${p}`" class="device-btn-icon"></vs-icon><span class="ml-2">{{ $t(p) }}</span></span>
        </div>
      </div>
    </div>

    <div class="vx-row">
      <div class="relative vx-col w-full mt-5">
        <!-- mockup -->
        <img class="w-full m-auto block mb-5" :class="previewMockupClasses" :src="mockupImgSrc" alt="Image" />

        <!-- mockup -->
        <img class="absolute m-auto block" v-if="file_as_data_url !== null && file_type === 'image'" :class="previewClasses" :src="file_as_data_url" />

        <video class="absolute" v-if="file_as_data_url !== null && file_type === 'video'" :class="previewClasses" :src="file_as_data_url"></video>
        <video class="absolute" v-if="this.getImageTypeBasedOnCampaignType() === 'default' && file_as_data_url !== null && file_type === 'video'" :class="previewBackImageClasses" :src="file_as_data_url"></video>

        <!-- content: this.value.content -->
        <img class="absolute m-auto block" v-if="!this.value.upload_video && this.getImageTypeBasedOnCampaignType() === 'default'" :class="previewBackImageClasses" :src="file_as_data_url" />

        <div class="flex justify-between absolute space-x-3" :class="previewContentBackImageClasses" v-if="this.getImageTypeBasedOnCampaignType() === 'default' && this.isAdContentEmpty()">
          <h4 class="cta-content-bg">{{ this.value.content }}</h4>

          <button v-if="showCTA" class="cta-preview-bg">{{this.value.cta.value}}</button>
        </div>

        <div class="flex justify-between absolute space-x-3" :class="previewContentClasses" v-if="this.getImageTypeBasedOnCampaignType() === 'default' && this.isAdContentEmpty()">
          <h4 class="cta-content">{{ this.value.content }}</h4>

          <button v-if="showCTA" class="cta-action">{{this.value.cta.value}}</button>
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
import './AdPreview.scss'

export default {
  props: {
    value: {
      required: true,
      type: Object
    },
    type: {
      type: String,
      required: true
    },
    language: {
      type: String,
      required: true
    }
  },
  watch: {
    value: {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    'value.file' () {
      this.initFilePreviewing()
    }
  },
  data () {
    return {
      // the relative data in each case
      previews: ['desktop', 'mobile', 'tablet'],
      types: ['default', 'inline', 'stories'],
      languages: ['arabic', 'english'],
      file_types: ['image', 'video'],
      contents: ['', '-no-content'],

      currently_previewing: 'desktop',
      file_as_data_url: null,
      file_type: null
    }
  },
  computed: {
    showCTA () {
      return this.$hasFeature('ad-cta') && this.type === this.$constants.CAMPAIGN_TYPES_NAMES.RECOMMENDATIONS && this.value.cta
    },
    mockupImgSrc () {
      // the name of the mockup is generated by the selected certeria
      // type-preview-language?-no-content
      // ex: default-desktop-arabic or default-desktop-arabic-no-content in case the ad content is empty
      // @see this.isAdContentEmpty

      const type = this.getImageTypeBasedOnCampaignType()
      const language = this.language
      const preview = this.currently_previewing
      let no_content = !this.isAdContentEmpty() ? '-no-content' : ''

      if (type === 'inline' || type === 'stories') {
        no_content = ''
      }

      return require(`@/assets/images/ad-preview/${type}-${preview}-${language}${no_content}.png`)
    },
    previewClasses () {
      // the name of the class is generated by the selected certeria
      // type-preview-language-file_type?-no-content
      // file type can be image or video
      // ex: default-desktop-arabic-image or default-desktop-arabic-image-no-content in case the ad content is empty
      // @see this.isAdContentEmpty
      const classes = {}

      const selected_type = this.getImageTypeBasedOnCampaignType()
      let no_content = !this.isAdContentEmpty() ? '-no-content' : ''

      if (selected_type === 'inline') {
        no_content = ''
      }

      this.previews.forEach(preview => {
        this.types.forEach((type) => {
          this.languages.forEach((language) => {
            this.file_types.forEach((file_type) => {
              this.contents.forEach((content) => {
                const result =
                  type === selected_type &&
                  this.currently_previewing === preview &&
                  this.language === language &&
                  this.file_type === file_type &&
                  no_content === content

                const className = `${type}-${preview}-${language}-${file_type}${content}`

                if (!classes.hasOwnProperty(className)) {
                  classes[className] = result
                }
              })
            })
          })
        })
      })

      return classes
    },
    previewMockupClasses () {
      // the name of the class is generated by the selected certeria
      // mockup-type-preview-language
      // file type can be image or video
      // ex: mockup-default-desktop-arabic or mockup-default-desktop-arabic-image in case the ad content is empty
      // @see this.isAdContentEmpty
      const classes = {}

      const selected_type = this.getImageTypeBasedOnCampaignType()

      this.previews.forEach(preview => {
        this.types.forEach((type) => {
          this.languages.forEach((language) => {
            const result =
                type === selected_type &&
                this.currently_previewing === preview &&
                this.language === language

            const className = `mockup-${type}-${preview}-${language}`

            if (!classes.hasOwnProperty(className)) {
              classes[className] = result
            }
          })
        })
      })

      return classes
    },
    previewBackImageClasses () {
      // the name of the class is generated by the selected certeria
      // back-type-preview-language-file_type?-no-content
      // file type can be image or video
      // ex: back-default-desktop-arabic-image or back-default-desktop-arabic-image-no-content in case the ad content is empty
      // @see this.isAdContentEmpty
      const classes = {}

      const selected_type = 'default'
      const no_content = !this.isAdContentEmpty() ? '-no-content' : ''
      const type = 'default'

      this.previews.forEach(preview => {
        this.languages.forEach((language) => {
          this.file_types.forEach((file_type) => {
            this.contents.forEach((content) => {
              const result =
                type === selected_type &&
                this.currently_previewing === preview &&
                this.language === language &&
                this.file_type === file_type &&
                no_content === content

              const className = `back-${type}-${preview}-${language}-${file_type}${content}`

              if (!classes.hasOwnProperty(className)) {
                classes[className] = result
              }
            })
          })
        })
      })

      return classes
    },
    previewContentBackImageClasses () {
      // the name of the class is generated by the selected certeria
      // content-back-type-preview-language-file_type?-no-content
      // file type can be image or video
      // ex: content-back-default-desktop-arabic-image or content-back-default-desktop-arabic-image-no-content in case the ad content is empty
      // @see this.isAdContentEmpty
      const classes = {}

      const selected_type = 'default'
      const no_content = !this.isAdContentEmpty() ? '-no-content' : ''
      const type = 'default'

      this.previews.forEach(preview => {
        this.languages.forEach((language) => {
          this.file_types.forEach((file_type) => {
            this.contents.forEach((content) => {
              const result =
                type === selected_type &&
                this.currently_previewing === preview &&
                this.language === language &&
                this.file_type === file_type &&
                no_content === content

              const className = `content-back-${type}-${preview}-${language}-${file_type}${content}`

              if (!classes.hasOwnProperty(className)) {
                classes[className] = result
              }
            })
          })
        })
      })

      return classes
    },
    previewContentClasses () {
      // the name of the class is generated by the selected certeria
      // content-type-preview-language-file_type?-no-content
      // file type can be image or video
      // ex: content-default-desktop-arabic-image or content-default-desktop-arabic-image-no-content in case the ad content is empty
      // @see this.isAdContentEmpty
      const classes = {}

      const selected_type = this.getImageTypeBasedOnCampaignType()
      let no_content = !this.isAdContentEmpty() ? '-no-content' : ''
      const types = ['default', 'stories']
      if (selected_type === 'inline') {
        no_content = ''
      }

      this.previews.forEach(preview => {
        types.forEach((type) => {
          this.languages.forEach((language) => {
            this.file_types.forEach((file_type) => {
              this.contents.forEach((content) => {
                const result =
                  type === selected_type &&
                  this.currently_previewing === preview &&
                  this.language === language &&
                  this.file_type === file_type &&
                  no_content === content

                const className = `content-${type}-${preview}-${language}-${file_type}${content}`

                if (!classes.hasOwnProperty(className)) {
                  classes[className] = result
                }
              })
            })
          })
        })
      })

      return classes
    }
  },
  mounted () {
    this.initFilePreviewing()
  },
  methods: {
    preview (file) {
      const reader = new FileReader()

      reader.onload = (e) => {
        this.file_as_data_url = e.target.result
      }

      if (file instanceof File || file instanceof Blob) {
        reader.readAsDataURL(file)
      }
    },
    initFilePreviewing () {
      if (this.value.file instanceof File) {
        this.file_type = this.value.file.type.split('/')[0]

        this.preview(this.value.file)
      } else if (typeof this.value.file === 'string' && this.value.file.includes('https://www.youtube.com/watch?v=')) {
        this.file_as_data_url = this.getThumpFromYoutubelink(this.value.file)
        this.file_type = 'image'
      } else {
        this.file_as_data_url = this.value.file || null
        if (this.file_as_data_url) {
          this.file_type = this.value.has_video ? 'video' : 'image'
        } else {
          this.file_type = null
        }
      }
    },
    changeCurrentlyPreviewing (p) {
      this.currently_previewing = p
    },
    getThumpFromYoutubelink (link) {
      let video_id = link.split('v=')[1]

      const ampersandPosition = video_id.indexOf('&')

      if (ampersandPosition !== -1) {
        video_id = video_id.substring(0, ampersandPosition)
      }

      return `https://img.youtube.com/vi/${video_id}/maxresdefault.jpg`
    },
    isAdContentEmpty () {
      return this.value.content !== '' && this.value.content !== null
    },
    getImageTypeBasedOnCampaignType () {
      let type = 'default'

      if (this.type === 'Inline' || this.type === 'Stories') {
        type = this.type.toLowerCase()
      }

      return type
    }
  }
}
</script>

<style lang="scss">
  .device-btn {
    width: 161px;
    height: 41px;
  }

  .device-btn-icon {
    position: relative;
    top: 4px;
  }

  .preview-container {
    .video-container {
      width: 70%;
      height: 100%;

      video {
        width: 100%;
      }
    }
  }

  .back-button-icon {
    position: absolute;
    top: -1px;
    left: -30px;
  }

  .cta {
    background: #f4f4f4;
    border: 1px solid #c2c2c2 !important;
    border-radius: 4px;
    display: inline-block;
    white-space: nowrap;
  }

  .cta-action {
    @extend .cta;

    padding: 0 12px;
    height: 36px;
    font: 14px Poppins;
  }

  .cta-preview {
    @extend .cta;

    padding: 6px;
    height: 23px;
    font-size: x-small;
  }

  .cta-preview-bg {
    @extend .cta;

    padding: 6px;
    font-size: 6px;
    height: 16px;
    line-height: 10%;
  }

  .cta-content {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .cta-content-bg {
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
  }

  .cta-action:hover {
    background: #d8dadf;
  }

  @media (max-width: 768px) {
    .device-btn {
      width: 100%;
    }

    .cta-mobile {
      background: #f4f4f4;
      border: 1px solid #323232 !important;
      border-radius: 4px;
      display: inline-block;
      white-space: nowrap;
    }

    .cta-action {
      @extend .cta-mobile;

      font-size: 5px;
      height: 10px;
      line-height: 10%;
      width: 45px;
      padding: 2px;
    }

    .cta-preview {
      @extend .cta-mobile;

      padding: 6px;
      height: 23px;
      font-size: x-small;
    }

    .cta-preview-bg {
      @extend .cta-mobile;

      font-size: 1px;
      padding: 0;
      height: 5px;
      min-width: 8px;
    }

    .cta-content-bg {
      overflow: hidden;
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
</style>
